import { PipeTransform, Pipe } from '@angular/core';
import { BetlimitService } from '../services/betlimit.service';
import { CurrencyConstant } from '../constant/currency.constant';

@Pipe({
  name: 'usercurrency',
  standalone: false,
})
export class UserCurrencyPipe implements PipeTransform {
  constructor(private readonly _betLimitService: BetlimitService) {}

  transform(
    val: any,
    maxDecimal = undefined, // This argument might become less relevant or need re-evaluation
    overrideCurrencyCode: string | null = null
  ): string {
    // Ensure 'val' is a number. If not, attempt to parse or return an error/default.
    const numericValue = parseFloat(val);
    if (isNaN(numericValue)) {
      // Handle non-numeric input: return original, empty string, or an error message
      // console.warn(`UserCurrencyPipe: Input '${val}' is not a valid number.`);
      return String(val); // Or return ''; or throw an error
    }

    // Use the overridden currency code if provided, otherwise use the service's currency code.
    let currencyCode = overrideCurrencyCode
      ? overrideCurrencyCode.toUpperCase()
      : this._betLimitService.currencyCode.toUpperCase();

    try {
      return formatCurrency(numericValue, currencyCode);
    } catch (error) {
      console.error(
        `Error formatting currency ${currencyCode} for value ${numericValue}:`,
        error
      );
      // Fallback for errors from formatCurrency (e.g., if config is truly missing and no fallback in formatCurrency)
      return `${numericValue}`; // Basic fallback
    }
  }
}

/**
 * Formats the integer part of a number string based on the specified rule and separator.
 * @param integerStr The integer part of the number as a string.
 * @param formatRule The formatting rule (e.g., 'A', 'E', 'Crypto').
 * @param thousandsSeparator The character to use as a thousands separator.
 * @returns Formatted integer string.
 */
function formatIntegerPart(
  integerStr: string,
  formatRule: CurrencyMapEntry['format'],
  thousandsSeparator: string
): string {
  if (thousandsSeparator === '') {
    return integerStr;
  }

  if (formatRule === 'E') {
    // Indian numbering system (e.g., 10,00,000)
    if (integerStr.length > 3) {
      const lastThree = integerStr.slice(-3);
      const otherNumbers = integerStr.slice(0, -3);
      if (otherNumbers) {
        // Apply grouping for every 2 digits on the 'otherNumbers' part
        const formattedOtherNumbers = otherNumbers.replace(
          /\B(?=(\d{2})+(?!\d))/g,
          thousandsSeparator
        );
        return formattedOtherNumbers + thousandsSeparator + lastThree;
      }
      // This case should ideally not be hit if length > 3 and otherNumbers is empty,
      // but as a fallback, return the lastThree.
      return lastThree;
    }
    return integerStr;
  }

  // Standard grouping by 3 for A, B, C, D, F and default
  return integerStr.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
}

type CurrencyMapEntry = {
  position: 'front' | 'back';
  hasSpacing: boolean;
  name: string;
  symbol: string;
  decimal: 'dot' | 'comma' | 0;
  format?: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'Crypto';
  numberFormat?: 'comma' | 'dot' | 'space' | 'apostrophe' | 'appost' | 'period';
  cryptoMaxDecimals?: number;
  hasSpacingSymbol?: boolean; // Custom property for fine-grained spacing control
  [key: string]: any;
};

/**
 * Formats a number according to the currency's specific configuration.
 * @param amount The number to format.
 * @param currencyCode The currency code (e.g., 'AED', 'USD', 'mBTC').
 * @returns The formatted currency string.
 * @throws Error if currency code is not found and no fallback is desired.
 */
export function formatCurrency(amount: number, currencyCode: string): string {
  const upperCurrencyCode = currencyCode.toUpperCase();
  let config = CurrencyConstant.CURRENCIES[upperCurrencyCode];

  if (!config) {
    // currncyCode might be a symbol
    config = Object.values(CurrencyConstant.CURRENCIES).find(
      (entry) => entry.symbol === upperCurrencyCode
    );
    if (config) {
      currencyCode = config.name; // Use the name for further processing
    }
  }

  if (!config && upperCurrencyCode !== 'MBTC') {
    // Allow MBTC to proceed even if not in full config for hardcoding
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode.toUpperCase(),
      }).format(amount);
    } catch (e) {
      return amount.toString();
    }
  }

  let fractionDigits: number;
  let actualDecimalSeparator: string;

  if (config.decimal === 0) {
    fractionDigits = 0;
    actualDecimalSeparator = ''; // No decimal separator if no decimal places
  } else {
    // For other currencies, use configuration
    fractionDigits = config.cryptoMaxDecimals ?? 2; // Default to 2 if not specified
    actualDecimalSeparator = config.decimal === 'dot' ? '.' : ',';
  }

  let thousandsSeparatorChar = '';
  // For mBTC, numberFormat would typically be undefined, leading to no thousands separator.
  // If other crypto coins need specific thousands separators, their config.numberFormat should be set.
  if (upperCurrencyCode !== 'MBTC' && config.numberFormat) {
    switch (config.numberFormat) {
      case 'comma':
        thousandsSeparatorChar = ',';
        break;
      case 'dot':
      case 'period':
        thousandsSeparatorChar = '.';
        break;
      case 'space':
        thousandsSeparatorChar = ' ';
        break;
      case 'apostrophe':
      case 'appost':
        thousandsSeparatorChar = "'";
        break;
      default:
        thousandsSeparatorChar = ''; // Should not be reached if numberFormat is defined
    }
  }

  // Special handling for MAD to use non-breaking spaces for thousands and decimal
  // if (upperCurrencyCode === 'MAD') {
  //   thousandsSeparatorChar = '\u00A0'; // Non-breaking space
  //   actualDecimalSeparator = '\u00A0'; // Non-breaking space
  // }

  const sign = amount < 0 ? '-' : '';
  const absAmount = Math.abs(amount);

  const numStr = absAmount.toFixed(fractionDigits);
  const parts = numStr.split('.'); // toFixed always uses '.' as separator

  const integerPartStr = parts[0];
  let fractionalPartStr = parts.length > 1 ? parts[1] : '';

  const formattedInteger = formatIntegerPart(
    integerPartStr,
    config.format,
    thousandsSeparatorChar
  );

  let formattedNumber: string;
  if (fractionDigits > 0 && fractionalPartStr) {
    // Only add decimal separator if there are fraction digits AND a fractional part string
    formattedNumber =
      formattedInteger + actualDecimalSeparator + fractionalPartStr;
  } else if (
    fractionDigits > 0 &&
    !fractionalPartStr &&
    actualDecimalSeparator
  ) {
    // Case: 0 decimals in input, but fractionDigits > 0 (e.g. 100 -> 100.00)
    // fractionalPartStr would be empty if toFixed(0).split('.')[1]
    // but toFixed(2) for 100 gives "100.00", so fractionalPartStr will be "00"
    // This case might be redundant if toFixed always provides the fractional part.
    // Let's assume toFixed(N) where N > 0 always gives X.Y...Y
    formattedNumber =
      formattedInteger + actualDecimalSeparator + '0'.repeat(fractionDigits);
  } else {
    formattedNumber = formattedInteger;
  }
  // Simpler logic after toFixed guarantees fractional part if fractionDigits > 0
  if (fractionDigits > 0) {
    if (fractionDigits > 0 && config?.cryptoMaxDecimals) {
      let i = fractionalPartStr.length - 1;
      while (i >= 2 && fractionalPartStr[i] === '0') {
        i--;
      }
      // Cut the fractional part at the determined index (i + 1)
      fractionalPartStr = fractionalPartStr.substring(0, i + 1);
    }
    formattedNumber =
      formattedInteger + actualDecimalSeparator + fractionalPartStr;
  } else {
    formattedNumber = formattedInteger;
  }

  return sign + formattedNumber;
}
